/* ============================================================================
   PERFIL VERTICAL — ascenso, crucero y descenso sobre el terreno.

   Convierte el perfil de terreno de la ruta en una senda vertical: subir desde
   el punto de despegue a un régimen y velocidad dados, mantener el nivel de
   crucero, y descender al punto de aterrizaje empezando en el último momento
   posible que deje la senda libre de obstáculos.

   Parámetros por defecto acordados con el operador:
     · Ascenso  700 ft/min a  70 kt
     · Descenso 800 ft/min a 105 kt

   Todos son editables: son perfiles de operación del piloto, no límites del
   fabricante. El margen de libramiento tampoco es un mínimo reglamentario —
   es un parámetro de la aplicación y la interfaz tiene que decirlo.

   Aritmética
   ----------
   El gradiente en pies por milla náutica es lo que importa, no el régimen
   suelto: 700 ft/min a 70 kt son 600 ft/NM, y 800 ft/min a 105 kt son
   457 ft/NM. A la misma velocidad vertical, más velocidad de avance significa
   una senda más plana, que es justo lo que decide si se libra un cerro.
   ========================================================================== */

/* Los números que lee el piloto se escriben con la convención del proyecto
   —coma decimal, punto de miles—, no con la del equipo donde corre. En un
   aviso de terreno la diferencia no es cosmética: «en la milla 12.0» se lee
   como doce mil con la convención castellana. */
import { numero } from "./formato.js";

/** Un nudo recorre esta fracción de milla náutica por minuto. */
const MINUTOS_POR_HORA = 60;

export const ASCENSO_POR_DEFECTO = { regimenFtMin: 700, velocidadKt: 70 };
export const DESCENSO_POR_DEFECTO = { regimenFtMin: 800, velocidadKt: 105 };

/**
 * Margen de libramiento sobre el terreno, en pies.
 *
 * NO es un mínimo reglamentario: los mínimos VFR de altura sobre el terreno
 * los fija cada autoridad y esta app todavía no los tiene cargados. Es un
 * colchón de la herramienta, editable, para que la senda no se dibuje rozando
 * la cima.
 */
export const MARGEN_LIBRAMIENTO_FT = 500;

/** Gradiente de la senda en pies por milla náutica. */
export function gradienteFtPorNm(regimenFtMin, velocidadKt) {
  if (!Number.isFinite(regimenFtMin) || !Number.isFinite(velocidadKt) || velocidadKt <= 0) return null;
  const nmPorMinuto = velocidadKt / MINUTOS_POR_HORA;
  if (nmPorMinuto <= 0) return null;
  return regimenFtMin / nmPorMinuto;
}

/**
 * Prepara el perfil una sola vez: filtra huecos y deja el arreglo listo para
 * búsqueda binaria.
 *
 * La versión anterior filtraba el perfil ENTERO y lo recorría linealmente en
 * cada consulta. Con 500 muestras de terreno y unas 12 000 consultas por
 * cálculo, eran 10 ms por llamada en un servidor — y el cálculo se rehace en
 * cada render, o sea en cada tecla. En un teléfono eso se siente como que la
 * aplicación se traba al escribir.
 */
function prepararTerreno(perfil) {
  if (!Array.isArray(perfil)) return [];
  return perfil
    .filter((p) => Number.isFinite(p.elevFt) && Number.isFinite(p.distNm))
    .sort((a, b) => a.distNm - b.distNm);
}

/** Altura del terreno en una distancia dada, interpolando entre muestras. */
function terrenoEn(preparado, distNm) {
  const n = preparado.length;
  if (n === 0) return null;
  if (distNm <= preparado[0].distNm) return preparado[0].elevFt;
  const ultimo = preparado[n - 1];
  if (distNm >= ultimo.distNm) return ultimo.elevFt;

  let bajo = 0;
  let alto = n - 1;
  while (alto - bajo > 1) {
    const medio = (bajo + alto) >> 1;
    if (preparado[medio].distNm <= distNm) bajo = medio;
    else alto = medio;
  }
  const a = preparado[bajo];
  const b = preparado[alto];
  const t = b.distNm === a.distNm ? 0 : (distNm - a.distNm) / (b.distNm - a.distNm);
  return a.elevFt + (b.elevFt - a.elevFt) * t;
}

/**
 * Altura de la senda vertical en una distancia dada.
 *
 * Tres tramos: ascenso hasta el top of climb, crucero nivelado, descenso desde
 * el top of descent.
 */
function sendaEn(distNm, { elevSalidaFt, elevLlegadaFt, cruceroFt, tocNm, todNm, gradAsc, gradDesc, totalNm, inicioAsc = 0 }) {
  if (distNm <= 0) return elevSalidaFt;
  if (distNm >= totalNm) return elevLlegadaFt;
  // Tramo nivelado antes de empezar a subir, si el piloto lo pidió.
  if (distNm <= inicioAsc) return elevSalidaFt;
  if (distNm <= tocNm) return elevSalidaFt + gradAsc * (distNm - inicioAsc);
  if (distNm >= todNm) return Math.max(elevLlegadaFt, cruceroFt - gradDesc * (distNm - todNm));
  return cruceroFt;
}

/**
 * Calcula el perfil vertical completo y verifica que libre el terreno.
 *
 * El descenso arranca en el último punto posible: se calcula hacia atrás desde
 * el aterrizaje. Si el terreno se mete en esa senda, se retrasa el inicio del
 * descenso hasta que la senda quede libre — y si retrasarlo obliga a un
 * gradiente mayor del elegido, la función lo declara IMPOSIBLE con el régimen
 * pedido en vez de dibujar una senda que no se puede volar.
 *
 * @param {object} opciones
 * @param {Array<{distNm:number, elevFt:number|null}>} opciones.perfilTerreno
 * @param {number} opciones.totalNm distancia total de la ruta
 * @param {number} opciones.elevSalidaFt elevación del punto de despegue
 * @param {number} opciones.elevLlegadaFt elevación del punto de aterrizaje
 * @param {number} opciones.cruceroFt nivel de crucero elegido
 */
export function calcularPerfilVertical({
  perfilTerreno = [],
  totalNm = 0,
  elevSalidaFt = null,
  elevLlegadaFt = null,
  cruceroFt = 0,
  ascenso = ASCENSO_POR_DEFECTO,
  descenso = DESCENSO_POR_DEFECTO,
  margenFt = MARGEN_LIBRAMIENTO_FT,
  todManualNm = null,
  inicioAscensoNm = 0,
} = {}) {
  const avisos = [];
  const terreno = prepararTerreno(perfilTerreno);
  const gradAsc = gradienteFtPorNm(ascenso.regimenFtMin, ascenso.velocidadKt);
  const gradDesc = gradienteFtPorNm(descenso.regimenFtMin, descenso.velocidadKt);

  if (!Number.isFinite(totalNm) || totalNm <= 0) {
    return { valido: false, avisos: ["La ruta no tiene distancia: fija el punto de despegue y el de aterrizaje."] };
  }
  if (gradAsc === null || gradDesc === null || gradAsc <= 0 || gradDesc <= 0) {
    return { valido: false, avisos: ["Régimen o velocidad inválidos: la velocidad tiene que ser mayor que cero."] };
  }
  // Sin cota de un extremo no hay perfil vertical posible. Antes se suponía el
  // nivel del mar: para un helipuerto en altura eso da un ascenso, un tiempo y
  // un consumo equivocados, con toda la apariencia de estar bien.
  if (!Number.isFinite(elevSalidaFt) || !Number.isFinite(elevLlegadaFt)) {
    return {
      valido: false,
      avisos: [
        !Number.isFinite(elevSalidaFt) && !Number.isFinite(elevLlegadaFt)
          ? "Faltan las cotas del punto de despegue y del de aterrizaje."
          : !Number.isFinite(elevSalidaFt)
            ? "Falta la cota del punto de despegue."
            : "Falta la cota del punto de aterrizaje.",
      ],
    };
  }

  const subidaFt = cruceroFt - elevSalidaFt;
  const bajadaFt = cruceroFt - elevLlegadaFt;
  if (subidaFt < 0) avisos.push("El nivel de crucero está por debajo del punto de despegue.");
  if (bajadaFt < 0) avisos.push("El nivel de crucero está por debajo del punto de aterrizaje.");

  // La referencia es la MÁS ALTA entre la elevación declarada del punto y la
  // del terreno bajo él. No siempre coinciden: la declarada la carga el piloto
  // o la trae la base de aeródromos, y la del terreno sale del modelo digital.
  // Usar la declarada cuando el terreno está más alto dejaba una franja fina
  // —45 ft en una prueba real— donde la senda quedaba fuera de la transición y
  // el cálculo declaraba imposible una ruta perfectamente volable.
  const refSalidaFt = Math.max(elevSalidaFt, terrenoEn(terreno, 0) ?? elevSalidaFt);
  const refLlegadaFt = Math.max(elevLlegadaFt, terrenoEn(terreno, totalNm) ?? elevLlegadaFt);
  const enTransicion = (sendaFt) =>
    sendaFt < refSalidaFt + margenFt || sendaFt < refLlegadaFt + margenFt;

  /* ── Ascenso: gradiente necesario para librar el terreno ─────────────────
   *
   * Antes el ascenso solo se avisaba; ahora se calcula. Se recorre el terreno
   * desde el despegue y, para cada punto, se ve qué gradiente haría falta para
   * pasar por encima con el margen. El mayor de todos manda.
   *
   * Solo se mira hasta donde la senda todavía está subiendo: más allá del
   * punto en que alcanzaría el nivel de crucero, el libramiento ya no es
   * problema del ascenso sino del crucero, que se verifica aparte.
   */
  /**
   * Punto donde arranca el ascenso.
   *
   * Por defecto es el despegue mismo. El piloto puede correrlo para volar
   * nivelado un tramo antes de subir —salir de un valle siguiendo el cauce en
   * vez de trepar contra la ladera es una maniobra normal en montaña—.
   */
  const inicioAsc = Number.isFinite(inicioAscensoNm) ? Math.max(0, Math.min(inicioAscensoNm, totalNm)) : 0;

  /**
   * Franja de despegue que este cálculo NO cubre.
   *
   * Mide lo que cuesta subir el margen al régimen elegido. Dentro de ella el
   * helicóptero está atravesando el propio margen y ninguna tasa de ascenso
   * finita libra una ladera que arranca a los pies de la plataforma: la
   * exigencia tendería a infinito al acercarse a la plataforma.
   *
   * El libramiento en esa franja es la trayectoria de despegue, que se decide
   * visualmente y con la carta del helipuerto — no con este perfil.
   */
  const transicionAscensoNm = margenFt / gradAsc;

  const gradienteAscensoNecesario = (() => {
    let necesario = 0;
    const alcanceNm = Math.max(0, subidaFt) / gradAsc;
    // Se explora algo más allá del alcance nominal: si hace falta subir más
    // rápido, el punto de nivelación se adelanta, no se atrasa.
    const hasta = Math.min(totalNm, Math.max(alcanceNm, 1) * 3);
    // Dentro de la primera franja el helicóptero está atravesando el propio
    // margen a su régimen normal: exigirle librarlo ahí daría gradientes
    // absurdos (a 0,15 NM de la plataforma pedía 3 900 ft/min sobre terreno
    // perfectamente plano). La franja mide justo lo que cuesta subir el margen.
    const desde = transicionAscensoNm;

    // Se recorren los VÉRTICES del terreno, no una grilla de paso fijo.
    //
    // El perfil es lineal entre muestras, así que el gradiente máximo que hace
    // falta siempre cae en un vértice. Una grilla de paso fijo se los saltea:
    // con paso de 0,3 NM el cálculo pasaba de 3,9 a 4,2 NM y no veía el borde
    // de un cerro que empezaba justo en 4,0. Resultado: proponía un régimen
    // que después el propio control de libramiento rechazaba.
    for (const punto of terreno) {
      const d = punto.distNm;
      if (d < inicioAsc + desde || d > inicioAsc + hasta) continue;
      const cota = punto.elevFt;
      if (cota === null) continue;
      // Terreno que no supera el punto de despegue no obliga a subir más rápido.
      if (cota <= refSalidaFt) continue;
      const objetivo = cota + margenFt;
      // Por encima del crucero no se sube: eso es problema del nivel elegido.
      if (objetivo > cruceroFt) continue;
      const g = d <= inicioAsc ? Infinity : (objetivo - elevSalidaFt) / (d - inicioAsc);
      if (!Number.isFinite(g)) continue;
      if (g > necesario) necesario = g;
    }
    return necesario;
  })();

  const ascensoAjustado = gradienteAscensoNecesario > gradAsc + 1;
  const gradAscUsado = ascensoAjustado ? gradienteAscensoNecesario : gradAsc;
  const regimenAscensoRequeridoFtMin = (gradAscUsado * ascenso.velocidadKt) / MINUTOS_POR_HORA;

  const tocNm = inicioAsc + Math.max(0, subidaFt) / gradAscUsado;
  const todNominalNm = totalNm - Math.max(0, bajadaFt) / gradDesc;

  // ── Descenso: se busca el último inicio posible que libre el terreno ──────
  //
  // Empezar más tarde deja la senda MÁS ALTA sobre el terreno del tramo, así
  // que retrasar el TOD es lo que resuelve un cerro en la bajada. El precio es
  // que queda menos distancia para perder la misma altura: el gradiente sube.
  let todNm = Number.isFinite(todManualNm) ? todManualNm : todNominalNm;
  let gradDescRequerido = gradDesc;
  let ajustadoPorTerreno = false;

  const muestrasDescenso = 60;

  /**
   * ¿Está el punto dentro de la transición de despegue o aterrizaje?
   *
   * El margen de libramiento no puede exigirse pegado al suelo: el helicóptero
   * arranca y termina en el terreno por definición, así que junto a los dos
   * extremos la senda SIEMPRE está por debajo de "terreno + margen". Sin esta
   * exclusión el cálculo declaraba imposible hasta una ruta sobre terreno
   * plano al nivel del mar.
   *
   * La transición termina cuando la senda ya subió el margen por encima del
   * punto de partida, y vuelve a empezar cuando baja a esa misma altura sobre
   * el de llegada. Dentro de esa franja el libramiento es criterio visual del
   * piloto, no una cuenta del perfil.
   */
  const violaTerreno = (inicio) => {
    const distanciaRestante = totalNm - inicio;
    if (distanciaRestante <= 0) return true;
    const gradiente = Math.max(0, bajadaFt) / distanciaRestante;
    for (let i = 0; i <= muestrasDescenso; i += 1) {
      const d = inicio + (distanciaRestante * i) / muestrasDescenso;
      const cota = terrenoEn(terreno, d);
      if (cota === null) continue;
      const senda = Math.max(elevLlegadaFt, cruceroFt - gradiente * (d - inicio));
      if (enTransicion(senda)) continue;
      if (senda < cota + margenFt) return true;
    }
    return false;
  };

  if (!Number.isFinite(todManualNm) && violaTerreno(todNm)) {
    ajustadoPorTerreno = true;
    const paso = totalNm / 200;
    let encontrado = null;
    for (let inicio = todNm + paso; inicio < totalNm; inicio += paso) {
      if (!violaTerreno(inicio)) { encontrado = inicio; break; }
    }
    if (encontrado === null) {
      avisos.push(
        "No hay ningún punto de inicio de descenso que libre el terreno con este nivel de crucero " +
        `y un margen de ${margenFt} ft. Sube el nivel de crucero o revisa la ruta.`,
      );
      return {
        valido: false, avisos,
        gradienteAscensoFtNm: gradAsc, gradienteDescensoFtNm: gradDesc,
        tocNm, todNominalNm,
      };
    }
    todNm = encontrado;
    gradDescRequerido = Math.max(0, bajadaFt) / (totalNm - todNm);
    const regimenRequerido = (gradDescRequerido * descenso.velocidadKt) / MINUTOS_POR_HORA;
    avisos.push(
      `El descenso se retrasó ${numero(todNm - todNominalNm, { decimales: 1 })} NM para librar el terreno. ` +
      `Eso exige ${Math.round(regimenRequerido)} ft/min a ${descenso.velocidadKt} kt, ` +
      `no los ${descenso.regimenFtMin} ft/min elegidos.`,
    );
  }

  if (Number.isFinite(todManualNm)) {
    gradDescRequerido = Math.max(0, bajadaFt) / Math.max(0.01, totalNm - todNm);
    if (violaTerreno(todNm)) {
      avisos.push("El inicio de descenso elegido a mano NO libra el terreno con el margen configurado.");
    }
  }

  if (tocNm > todNm) {
    avisos.push(
      "La ruta es demasiado corta para subir al nivel de crucero y volver a bajar: " +
      "el ascenso y el descenso se superponen. No hay tramo de crucero.",
    );
  }

  /* ── Crucero: ¿el nivel elegido libra el terreno del tramo nivelado? ──────
   *
   * Esto faltaba y era grave. Solo se verificaban el ascenso y el descenso, y
   * como la búsqueda del descenso solo mira desde el TOD hacia adelante,
   * retrasar el TOD "resolvía" un cerro simplemente por dejar de mirarlo: el
   * cerro pasaba a estar en el tramo de crucero, que nadie comprobaba.
   */
  let obstaculoCrucero = null;
  //
  // Se recorren los VÉRTICES del terreno, igual que en el ascenso. Una grilla
  // de paso fijo se saltea las cimas: con 1 NM de paso sobre una ruta de 125 NM
  // el control leía 11 426 ft donde el pico real es 11 459, y proponía un
  // crucero mínimo 33 ft más bajo del que hace falta. El error iba hacia el
  // lado peligroso —autorizar un nivel insuficiente— y encima hacía que el
  // gráfico y el resultado se contradijeran a la vista.
  for (const punto of terreno) {
    const d = punto.distNm;
    if (d < tocNm || d > todNm || d > totalNm) continue;
    const cota = punto.elevFt;
    if (cota === null) continue;
    if (enTransicion(cruceroFt)) continue;
    if (cruceroFt < cota + margenFt) {
      const faltanteFt = cota + margenFt - cruceroFt;
      if (!obstaculoCrucero || faltanteFt > obstaculoCrucero.faltanteFt) {
        obstaculoCrucero = { distNm: d, terrenoFt: cota, sendaFt: cruceroFt, faltanteFt };
      }
    }
  }
  if (obstaculoCrucero) {
    avisos.push(
      `El nivel de crucero de ${numero(Math.round(cruceroFt))} ft NO libra el terreno: en la milla ` +
      `${numero(obstaculoCrucero.distNm, { decimales: 1 })} hay ${numero(Math.round(obstaculoCrucero.terrenoFt))} ft, ` +
      `${Math.round(obstaculoCrucero.faltanteFt)} ft por encima de la senda con el margen configurado. ` +
      `Harían falta al menos ${numero(Math.round(obstaculoCrucero.terrenoFt + margenFt))} ft.`,
    );
  }

  // ── Ascenso: ¿el terreno se mete en la senda de subida? ───────────────────
  //
  // Se recorren los mismos VÉRTICES y se aplica la MISMA franja de transición
  // que el cálculo del gradiente. Con criterios distintos los dos bucles se
  // contradecían: uno proponía un régimen y el otro lo rechazaba por 39 ft en
  // un punto que el primero no había mirado.
  let obstaculoAscenso = null;
  for (const punto of terreno) {
    const d = punto.distNm;
    if (d < inicioAsc + transicionAscensoNm || d > tocNm) continue;
    const cota = punto.elevFt;
    if (cota === null) continue;
    const senda = d <= inicioAsc ? elevSalidaFt : elevSalidaFt + gradAscUsado * (d - inicioAsc);
    if (senda < cota + margenFt) {
      const faltanteFt = cota + margenFt - senda;
      if (!obstaculoAscenso || faltanteFt > obstaculoAscenso.faltanteFt) {
        obstaculoAscenso = { distNm: d, terrenoFt: cota, sendaFt: senda, faltanteFt };
      }
    }
  }
  if (ascensoAjustado) {
    avisos.push(
      `El ascenso se calculó en ${Math.round(regimenAscensoRequeridoFtMin)} ft/min a ` +
      `${ascenso.velocidadKt} kt (${Math.round(gradAscUsado)} ft/NM) para librar el terreno con ` +
      `${margenFt} ft de margen. Los ${ascenso.regimenFtMin} ft/min elegidos no alcanzan: ` +
      "comprueba que la aeronave pueda sostener ese régimen con el peso y la altitud de hoy.",
    );
  }
  if (obstaculoAscenso) {
    avisos.push(
      `Ni siquiera a ${Math.round(regimenAscensoRequeridoFtMin)} ft/min se libra el terreno en la ` +
      `milla ${numero(obstaculoAscenso.distNm, { decimales: 1 })}: faltan ` +
      `${Math.round(obstaculoAscenso.faltanteFt)} ft. Hay que salir en otra dirección o subir el ` +
      "nivel de crucero.",
    );
  }

  // ── Serie para dibujar, alineada con las muestras del perfil de terreno ───
  const distancias = new Set([0, inicioAsc, tocNm, todNm, totalNm]);
  perfilTerreno.forEach((p) => { if (Number.isFinite(p.distNm)) distancias.add(p.distNm); });
  const senda = [...distancias].sort((a, b) => a - b).map((d) => ({
    distNm: Math.round(d * 100) / 100,
    sendaFt: Math.round(sendaEn(d, {
      elevSalidaFt, elevLlegadaFt, cruceroFt, tocNm, todNm, inicioAsc,
      gradAsc: gradAscUsado, gradDesc: gradDescRequerido, totalNm,
    })),
  }));

  const minutosAscenso = regimenAscensoRequeridoFtMin > 0
    ? Math.max(0, subidaFt) / regimenAscensoRequeridoFtMin
    : 0;
  const minutosDescenso = descenso.regimenFtMin > 0
    ? (Math.max(0, bajadaFt) / ((gradDescRequerido * descenso.velocidadKt) / MINUTOS_POR_HORA))
    : 0;

  return {
    valido: true,
    avisos,
    gradienteAscensoFtNm: Math.round(gradAscUsado),
    gradienteAscensoElegidoFtNm: Math.round(gradAsc),
    ascensoAjustado,
    transicionAscensoNm: Math.round(transicionAscensoNm * 100) / 100,
    regimenAscensoRequeridoFtMin: Math.round(regimenAscensoRequeridoFtMin),
    gradienteDescensoFtNm: Math.round(gradDescRequerido),
    inicioAscensoNm: Math.round(inicioAsc * 10) / 10,
    tocNm: Math.round(tocNm * 10) / 10,
    todNm: Math.round(todNm * 10) / 10,
    todNominalNm: Math.round(todNominalNm * 10) / 10,
    ajustadoPorTerreno,
    minutosAscenso: Math.round(minutosAscenso),
    minutosDescenso: Math.round(minutosDescenso),
    regimenDescensoRequeridoFtMin: Math.round((gradDescRequerido * descenso.velocidadKt) / MINUTOS_POR_HORA),
    obstaculoAscenso,
    obstaculoCrucero,
    libraElTerreno: !obstaculoAscenso && !obstaculoCrucero && !violaTerreno(todNm),
    senda,
  };
}

/**
 * Combustible por tramo del perfil vertical.
 *
 * Ascenso, crucero y descenso se vuelan a altitudes y velocidades distintas, y
 * la carta de consumo depende de la altitud. Estimar el vuelo entero con el
 * consumo de crucero subestima el ascenso —que es donde más se gasta— y
 * sobrestima el descenso.
 *
 * Cada tramo se evalúa con su ALTITUD MEDIA: la mitad del ascenso, el nivel de
 * crucero, la mitad del descenso. Es una aproximación, y la app tiene que
 * decirlo: el consumo real depende del régimen de potencia que se use, y la
 * carta no está indexada por eso.
 *
 * Si la carta no cubre alguna de esas altitudes, ese tramo queda en null. No
 * se extrapola: un consumo inventado se ve igual que uno leído.
 *
 * @param {object} perfil resultado de calcularPerfilVertical
 * @param {(altFt:number)=>number|null} consumoEn devuelve kg/h a esa altitud
 * @param {number} temperaturaC solo para el mensaje; la carta la usa quien pasa consumoEn
 */
export function combustiblePorTramo(perfil, consumoEn, velocidadCruceroKt) {
  if (!perfil?.valido || typeof consumoEn !== "function") {
    return { valido: false, tramos: [], totalKg: null, avisos: ["Sin perfil vertical calculado."] };
  }

  const {
    inicioAscensoNm = 0, tocNm, todNm, senda,
    minutosAscenso, minutosDescenso,
  } = perfil;

  const altEn = (d) => {
    const p = senda.find((x, i) => i === senda.length - 1 || (d >= x.distNm && d <= senda[i + 1].distNm));
    return p ? p.sendaFt : null;
  };

  const elevSalida = senda[0]?.sendaFt ?? 0;
  const elevLlegada = senda[senda.length - 1]?.sendaFt ?? 0;
  const crucero = altEn((tocNm + todNm) / 2) ?? elevSalida;

  const distanciaCrucero = Math.max(0, todNm - tocNm);
  const minutosCrucero = Number.isFinite(velocidadCruceroKt) && velocidadCruceroKt > 0
    ? (distanciaCrucero / velocidadCruceroKt) * 60
    : null;
  const minutosNivelado = inicioAscensoNm > 0 && Number.isFinite(velocidadCruceroKt) && velocidadCruceroKt > 0
    ? (inicioAscensoNm / velocidadCruceroKt) * 60
    : 0;

  const definicion = [
    { clave: "nivelado", etiqueta: "Nivelado antes de subir", minutos: minutosNivelado, altitudFt: elevSalida },
    { clave: "ascenso", etiqueta: "Ascenso", minutos: minutosAscenso, altitudFt: (elevSalida + crucero) / 2 },
    { clave: "crucero", etiqueta: "Crucero", minutos: minutosCrucero, altitudFt: crucero },
    { clave: "descenso", etiqueta: "Descenso", minutos: minutosDescenso, altitudFt: (crucero + elevLlegada) / 2 },
  ].filter((t) => Number.isFinite(t.minutos) && t.minutos > 0);

  const avisos = [];
  const tramos = definicion.map((t) => {
    const kgPorHora = consumoEn(t.altitudFt);
    if (!Number.isFinite(kgPorHora)) {
      avisos.push(`${t.etiqueta}: la carta de consumo no cubre ${numero(Math.round(t.altitudFt))} ft. Ese tramo queda sin estimar.`);
      return { ...t, kgPorHora: null, kg: null };
    }
    return { ...t, kgPorHora, kg: Math.round((kgPorHora * t.minutos) / 60 * 10) / 10 };
  });

  const conDato = tramos.filter((t) => t.kg !== null);
  const totalKg = conDato.length === tramos.length && conDato.length > 0
    ? Math.round(conDato.reduce((s, t) => s + t.kg, 0) * 10) / 10
    : null;

  if (totalKg === null && conDato.length > 0) {
    avisos.push("El total no se puede sumar mientras haya tramos sin estimar: sería menor que el consumo real.");
  }
  if (minutosCrucero === null) {
    avisos.push("Sin velocidad de crucero no se puede estimar el tiempo del tramo nivelado.");
  }

  return {
    valido: true,
    tramos,
    totalKg,
    minutosTotales: Math.round(tramos.reduce((s, t) => s + t.minutos, 0)),
    avisos,
  };
}
